<template>
  <div class="ProfileDetails">
    <div class="ProfileDetails__content">
      <template v-if="step === 'form'">
        <h1 class="ProfileDetails__title">{{ $t('registration_profile_details_title') }}</h1>
        <p class="ProfileDetails__lede">
          {{ $t('registration_profile_details_subtitle') }}
          <span class="ProfileDetails__promise">{{ $t('registration_profile_details_promise') }}</span>
        </p>

        <div class="ProfileDetails__form">
          <div class="ProfileDetails__value">
            <LLInputText
              v-model.trim="occupation"
              :placeholder="$t('placeholder_occupation')"
              data-e2e="profileDetailsOccupation"
            >
              <template #caption>
                {{ $t('placeholder_occupation') }}
                <span class="ProfileDetails__optional">{{
                  $t('registration_profile_details_optional')
                }}</span>
              </template>
            </LLInputText>
          </div>

          <div class="ProfileDetails__value">
            <LLTextarea
              v-model="aboutMe"
              size="compact"
              dynamic-size
              plain-text
              hide-menu-bar
              hide-emoji
              :max-height="200"
              :max-length="200"
              :placeholder="$t('placeholder_about_me_hint')"
              data-e2e="profileDetailsAboutMe"
            >
              <template #caption>
                {{ $t('placeholder_about_me') }}
                <span class="ProfileDetails__optional">{{
                  $t('registration_profile_details_optional')
                }}</span>
              </template>
              <template #bottom-content>
                <div class="ProfileDetails__counter">{{ aboutMeLength }} / 200</div>
              </template>
            </LLTextarea>
          </div>

          <div class="ProfileDetails__value">
            <div class="ProfileDetails__label">
              {{ $t('registration_profile_details_interests_label') }}
              <span class="ProfileDetails__optional">{{
                $t('registration_profile_details_interests_hint')
              }}</span>
            </div>
            <UserEditModalInterestsList
              chips
              :user-interests-list.sync="interestIdList"
              data-e2e="profileDetailsInterests"
            ></UserEditModalInterestsList>
          </div>
        </div>

        <ProfileStrengthMeter
          class="ProfileDetails__meter"
          :occupation="occupation"
          :about-me="aboutMe"
          :interests-count="interestIdList.length"
        ></ProfileStrengthMeter>

        <div class="ProfileDetails__actions">
          <LLButton :action="onFinish" :loading="savePending" data-e2e="profileDetailsFinish">
            {{ $t('registration_profile_details_finish') }}
          </LLButton>
          <button
            type="button"
            class="ProfileDetails__skip"
            :disabled="savePending"
            data-e2e="profileDetailsSkip"
            @click="onSkip"
          >
            {{ $t('registration_profile_details_skip') }}
          </button>
        </div>
      </template>

      <div v-else class="ProfileDetails__done" data-e2e="profileDetailsDoneScreen">
        <div class="ProfileDetails__done-mark">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true">
            <path d="M4 12.5 9.5 18 20 6.5" />
          </svg>
        </div>
        <h1 class="ProfileDetails__title">
          {{ $t('registration_profile_details_done_title', { name: firstName }) }}
        </h1>
        <p class="ProfileDetails__lede">{{ $t('registration_profile_details_done_text') }}</p>
        <div class="ProfileDetails__actions">
          <LLButton :action="onDone" data-e2e="profileDetailsDoneButton">
            {{ $t('registration_profile_details_done_button') }}
          </LLButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LLButton from '@/components/common/ui-components/LLButton'
import LLInputText from '@/components/common/LLInput/LLInputText'
import LLTextarea from '@/components/common/LLTextarea.vue'
import UserEditModalInterestsList from '@/components/user/UserEditModal/UserEditModalInterestsList.vue'
import ProfileStrengthMeter from '@/components/pages/registration/ProfileStrengthMeter.vue'
import { ProspectModel } from '@/models/prospect'

export default {
  name: 'ProfileDetails',
  components: { LLButton, LLInputText, LLTextarea, UserEditModalInterestsList, ProfileStrengthMeter },
  data() {
    return {
      step: 'form',
      occupation: '',
      aboutMe: '',
      interestIdList: [],
      savePending: false
    }
  },
  computed: {
    user() {
      return ProspectModel.parseFromApi({ prospect: this.$store.state.user.user })
    },
    firstName() {
      return this.$store.state.user.user.firstName
    },
    aboutMeLength() {
      return (this.aboutMe || '').length
    }
  },
  created() {
    this.occupation = this.user.occupation || ''
    this.aboutMe = this.user.aboutMe || ''
    this.interestIdList = [...(this.user.interestIdList || [])]
  },
  methods: {
    /**
     * Finish writes the optional fields and dismisses the page. Skip only dismisses
     * it, so skipping writes nothing (LL-169, requirement 1.3).
     */
    async dismiss(payload) {
      this.savePending = true
      try {
        await this.$store.dispatch('user/syncUser', {
          ...payload,
          registrationDetailsDismissed: true
        })
        window.scrollTo({ top: 0, behavior: 'smooth' })
        this.step = 'done'
      } catch (e) {
        this.$errorAlert(e)
      }
      this.savePending = false
    },
    onFinish() {
      return this.dismiss({
        occupation: this.occupation || null,
        aboutMe: this.aboutMe || null,
        interestIdList: this.interestIdList
      })
    },
    onSkip() {
      return this.dismiss({})
    },
    onDone() {
      // Same destination as the selfie page's Done button today — the Dashboard
      // route guard applies the study/chat destination redirect when present.
      return this.$router.push({ name: 'Dashboard' })
    }
  }
}
</script>

<style lang="scss" scoped>
.ProfileDetails {
  @apply flex h-full;
  &__content {
    @apply m-auto py-10;
    width: 100%;
    max-width: 560px;
    @screen md-max {
      @apply px-4 pt-4;
    }
  }
  &__title {
    @apply text-center;
  }
  &__lede {
    @apply text-center text-body-02 text-neutral-01-500 mt-2 mb-6;
  }
  &__promise {
    @apply text-success-01 font-semibold;
  }
  &__form {
    @apply grid grid-cols-1 gap-5;
  }
  &__value {
    @apply w-full;
  }
  &__label {
    @apply font-semibold text-body-02 mb-2;
  }
  &__optional {
    @apply font-normal text-neutral-01-400;
  }
  &__counter {
    @apply text-caption-02 text-neutral-01-400 text-right mt-1;
  }
  &__meter {
    @apply mt-6;
  }
  &__actions {
    @apply mt-8 flex flex-col items-center;
    gap: 14px;
  }
  &__skip {
    @apply text-body-02 text-neutral-01-400 underline;
    text-underline-offset: 3px;

    &:hover {
      @apply text-neutral-01-500;
    }
  }
  &__done {
    @apply text-center pt-6;
  }
  &__done-mark {
    @apply w-16 h-16 rounded-full bg-success-01 flex items-center justify-center mx-auto mb-5;
    color: #fff;

    svg {
      width: 30px;
      height: 30px;
    }
  }
}
</style>
